import { useNavigate } from 'react-router-dom';
import { ExclamationTriangleIcon, ServerStackIcon } from '@heroicons/react/24/outline';
import type { NodeInfo } from '../../contexts/NodeContext';
import { useAuth } from '../../contexts/AuthContext';
import { BTN_PRIMARY, BTN_SECONDARY } from '@trinetra/shared';

/**
 * Stands in for a node-scoped page whose node is offline.
 *
 * Every one of those pages reads its data through the node's tunnel, so with the node down they
 * render as an endless spinner or an empty table — which reads as "no cameras" rather than "the
 * site is unreachable". This says which it is, and points at what is still worth looking at.
 */
export default function NodeOfflineNotice({ node }: { node: NodeInfo }) {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  const lastSeen = node.lastSeen
    ? new Date(node.lastSeen).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
    : null;

  return (
    <div className="flex min-h-[60vh] items-center justify-center p-4">
      <div className="w-full max-w-md rounded-xl border border-border bg-card p-6 text-center shadow-sm">
        <div
          className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10 text-destructive"
        >
          <ExclamationTriangleIcon className="h-6 w-6" />
        </div>

        <h2 className="text-lg font-semibold text-foreground">Site is offline</h2>

        <p className="mt-2 flex items-center justify-center gap-1.5 text-sm text-muted-foreground">
          <ServerStackIcon className="h-4 w-4 flex-shrink-0" />
          <span className="font-medium text-foreground">{node.name}</span>
        </p>

        <p className="mt-3 text-sm text-muted-foreground">
          This page reads live data from the site, so it can't be shown until it reconnects.
          The cloud reconnects on its own — nothing to do here.
        </p>

        {lastSeen && <p className="mt-2 text-xs text-muted-foreground">Last seen {lastSeen}</p>}

        <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
          <button
            onClick={() => navigate('/')}
            className={BTN_PRIMARY}
          >
            Go to Dashboard
          </button>
          {/* NOT /node-monitor/:nodeId — that page pulls its metrics through the same dead
              tunnel, so it is empty for exactly the nodes you'd click this for. The System
              Monitor's Sites tab is served by the cloud from the last heartbeat, so it
              still shows this node's status and last-seen time. */}
          {hasPermission('monitoring.view') && (
            <button
              onClick={() => navigate('/platform-health?tab=local-nodes')}
              className={BTN_SECONDARY}
            >
              View Node Status
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
